import type { Instrument, Timeframe } from "@trader/shared/MarketData"
import { useSyncExternalStore } from "react"
import type { StrategyInstance } from "../workspace/workspaceStore.ts"

// Starting a replay (docs/design/0007): "Replay" waits for a click on a bar, and
// the click opens the dialog that picks the paper account to replay it on. The
// replay itself is that account's clock; this store only holds the steps before it.

export interface PickedBar {
  readonly instrument: Instrument
  readonly timeframe: Timeframe
  /** Open time of the clicked bar: the replay starts once it has closed. */
  readonly barTime: number
  /** The chart (pane index) the bar was picked on, and its strategy (it can paper trade the replay). */
  readonly pane: number
  readonly strategy: StrategyInstance | null
}

export type ReplaySessionState =
  | { readonly status: "idle" }
  | { readonly status: "picking" }
  | { readonly status: "choosing"; readonly picked: PickedBar }

export const createReplaySession = () => {
  let state: ReplaySessionState = { status: "idle" }
  const listeners = new Set<() => void>()
  const set = (next: ReplaySessionState) => {
    state = next
    for (const listener of listeners) listener()
  }
  return {
    getState: (): ReplaySessionState => state,
    subscribe: (listener: () => void): (() => void) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    /** "Replay": the next click on a chart's bar picks where the replay starts. */
    pick: (): void => set({ status: "picking" }),
    /** A bar was clicked while picking. */
    picked: (picked: PickedBar): void => {
      if (state.status === "picking") set({ status: "choosing", picked })
    },
    /** Cancelled, or the replay started. */
    done: (): void => {
      if (state.status !== "idle") set({ status: "idle" })
    }
  }
}

export const replaySession = createReplaySession()

export const useReplaySession = (): ReplaySessionState => useSyncExternalStore(replaySession.subscribe, replaySession.getState)
